import { useState, useEffect, useCallback } from 'react';
import { ArrowUpDown, ChevronLeft, ChevronRight, Download, FileText, Layers, Settings, Users } from 'lucide-react';
import { vendorCostAnalysisApi } from '../../services/vendorCostAnalysisApi';
import { TransactionDrillDownModal } from './TransactionDrillDownModal';
import { ContextMenu, type ContextMenuItem } from '../ui/ContextMenu';
import type { VCAFilters, VCAVendorRow, VCAVendorGroupAggRow, PaginationInfo, VCAViewMode, VCATotals } from '../../types/vendorCostAnalysis';

interface Props {
    filters: VCAFilters;
    refreshKey: number;
    viewMode: VCAViewMode;
    onViewModeChange: (mode: VCAViewMode) => void;
    onNavigateToGroup?: (groupName: string) => void;
}

function formatEUR(n: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
}

export function ByVendorTab({ filters, refreshKey, viewMode, onViewModeChange, onNavigateToGroup }: Props) {
    const [vendorRows, setVendorRows] = useState<VCAVendorRow[]>([]);
    const [groupRows, setGroupRows] = useState<VCAVendorGroupAggRow[]>([]);
    const [pagination, setPagination] = useState<PaginationInfo | null>(null);
    const [totals, setTotals] = useState<VCATotals | null>(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState('totalEUR');
    const [sortOrder, setSortOrder] = useState('DESC');
    const [drillDown, setDrillDown] = useState<{ vendorID?: string; groupVendorID?: string; categoryCode?: string; title: string } | null>(null);
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; row: VCAVendorRow | VCAVendorGroupAggRow } | null>(null);

    useEffect(() => {
        setLoading(true);
        if (viewMode === 'vendor') {
            vendorCostAnalysisApi.getByVendor({
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                company: filters.company || undefined,
                categoryCode: filters.categoryCode || undefined,
                vendorIDs: filters.vendorIDs.length > 0 ? filters.vendorIDs : undefined,
                sortBy, sortOrder,
                page, limit: 25,
            })
                .then(res => { setVendorRows(res.items); setPagination(res.pagination); setTotals(res.totals); })
                .catch(() => { setVendorRows([]); setTotals(null); })
                .finally(() => setLoading(false));
        } else {
            vendorCostAnalysisApi.getByVendorGroup({
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                company: filters.company || undefined,
                categoryCode: filters.categoryCode || undefined,
                groupVendorIDs: filters.groupVendorIDs.length > 0 ? filters.groupVendorIDs : undefined,
                sortBy, sortOrder,
                page, limit: 25,
            })
                .then(res => { setGroupRows(res.items); setPagination(res.pagination); setTotals(res.totals); })
                .catch(() => { setGroupRows([]); setTotals(null); })
                .finally(() => setLoading(false));
        }
    }, [filters, sortBy, sortOrder, page, refreshKey, viewMode]);

    const handleExport = useCallback(async () => {
        setExporting(true);
        try {
            await vendorCostAnalysisApi.exportByVendor({
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                company: filters.company || undefined,
                categoryCode: filters.categoryCode || undefined,
                vendorIDs: filters.vendorIDs.length > 0 ? filters.vendorIDs : undefined,
                groupVendorIDs: filters.groupVendorIDs.length > 0 ? filters.groupVendorIDs : undefined,
                viewMode,
            });
        } catch { /* ignore */ }
        setExporting(false);
    }, [filters, viewMode]);

    useEffect(() => { setPage(1); }, [filters, viewMode]);

    const handleSort = useCallback((col: string) => {
        if (sortBy === col) {
            setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortBy(col);
            setSortOrder('DESC');
        }
        setPage(1);
    }, [sortBy]);

    const handleContextMenu = useCallback((e: React.MouseEvent, row: VCAVendorRow | VCAVendorGroupAggRow) => {
        e.preventDefault();
        setContextMenu({ x: e.clientX, y: e.clientY, row });
    }, []);

    const SortIcon = ({ col }: { col: string }) => (
        <ArrowUpDown className={`w-3 h-3 inline ml-1 ${sortBy === col ? 'text-blue-600' : 'text-gray-300'}`} />
    );

    const getContextMenuItems = (): ContextMenuItem[] => {
        if (!contextMenu) return [];
        const row = contextMenu.row;

        if (viewMode === 'vendor' && 'vendorCode' in row) {
            const vendorID = `${row.companyCode}-${row.vendorCode}`;
            return [
                {
                    label: 'Show All Transactions',
                    icon: <FileText className="w-4 h-4" />,
                    onClick: () => setDrillDown({ vendorID, title: `Transactions: ${row.vendorName} (${row.companyCode})` }),
                },
                {
                    label: 'Show by Category',
                    icon: <Layers className="w-4 h-4" />,
                    onClick: () => setDrillDown({ vendorID, title: `By Category: ${row.vendorName}` }),
                },
                {
                    label: 'Switch to Vendor Group View',
                    icon: <Users className="w-4 h-4" />,
                    onClick: () => onViewModeChange('vendorGroup'),
                },
            ];
        } else if ('groupVendorID' in row) {
            return [
                {
                    label: 'Show All Transactions',
                    icon: <FileText className="w-4 h-4" />,
                    onClick: () => setDrillDown({ groupVendorID: String(row.groupVendorID), title: `Transactions: ${row.groupVendorName}` }),
                },
                {
                    label: 'Modify Group',
                    icon: <Settings className="w-4 h-4" />,
                    onClick: () => onNavigateToGroup?.(row.groupVendorName),
                },
                {
                    label: 'Switch to Individual Vendor View',
                    icon: <Users className="w-4 h-4" />,
                    onClick: () => onViewModeChange('vendor'),
                },
            ];
        }
        return [];
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    const exportButton = (
        <div className="flex justify-end mb-2">
            <button
                onClick={handleExport}
                disabled={exporting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 border rounded-md hover:bg-gray-50 disabled:opacity-50"
                title="Download visible rows as Excel"
            >
                <Download className="w-3.5 h-3.5" />
                {exporting ? 'Exporting…' : 'Download Excel'}
            </button>
        </div>
    );

    // Vendor mode table
    if (viewMode === 'vendor') {
        return (
            <div>
                {exportButton}
                <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_rgba(0,0,0,0.08)]">
                        <tr className="text-left text-xs font-medium text-gray-500 uppercase border-b">
                            <th className="py-2 pr-3 cursor-pointer bg-white" onClick={() => handleSort('vendorName')}>
                                Vendor <SortIcon col="vendorName" />
                            </th>
                            <th className="py-2 pr-3 cursor-pointer bg-white" onClick={() => handleSort('companyCode')}>
                                Company <SortIcon col="companyCode" />
                            </th>
                            <th className="py-2 pr-3 cursor-pointer bg-white" onClick={() => handleSort('country')}>
                                Country <SortIcon col="country" />
                            </th>
                            <th className="py-2 pr-3 text-right cursor-pointer bg-white" onClick={() => handleSort('totalEUR')}>
                                Total EUR <SortIcon col="totalEUR" />
                            </th>
                            <th className="py-2 pr-3 text-right cursor-pointer bg-white" onClick={() => handleSort('totalEURPY')}>
                                PY EUR <SortIcon col="totalEURPY" />
                            </th>
                            <th className="py-2 pr-3 text-right bg-white">Delta %</th>
                            <th className="py-2 text-right cursor-pointer bg-white" onClick={() => handleSort('documentCount')}>
                                Docs <SortIcon col="documentCount" />
                            </th>
                        </tr>
                        {totals && vendorRows.length > 0 && (
                            <tr className="border-b border-gray-200 bg-gray-50 text-sm font-semibold text-gray-700">
                                <td className="py-2 pr-3 bg-gray-50" colSpan={3}>
                                    Total ({pagination?.total ?? vendorRows.length} vendors)
                                </td>
                                <td className="py-2 pr-3 text-right font-mono bg-gray-50">{formatEUR(totals.totalEUR)}</td>
                                <td className="py-2 pr-3 text-right font-mono text-gray-500 bg-gray-50">{formatEUR(totals.totalEURPY)}</td>
                                <td className="py-2 pr-3 text-right bg-gray-50">
                                    {totals.deltaPct !== null ? (
                                        <span className={totals.deltaPct > 0 ? 'text-red-600' : totals.deltaPct < 0 ? 'text-green-600' : ''}>
                                            {totals.deltaPct > 0 ? '+' : ''}{totals.deltaPct.toFixed(1)}%
                                        </span>
                                    ) : <span className="text-gray-300">-</span>}
                                </td>
                                <td className="bg-gray-50"></td>
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {vendorRows.map((r, i) => (
                            <tr
                                key={i}
                                className="border-b border-gray-100 hover:bg-blue-50 cursor-pointer"
                                onClick={() => {
                                    const vendorID = `${r.companyCode}-${r.vendorCode}`;
                                    setDrillDown({ vendorID, title: `Transactions: ${r.vendorName} (${r.companyCode})` });
                                }}
                                onContextMenu={e => handleContextMenu(e, r)}
                            >
                                <td className="py-2 pr-3">
                                    <span className="font-medium">{r.vendorName}</span>
                                    <span className="text-gray-400 ml-1">({r.vendorCode})</span>
                                </td>
                                <td className="py-2 pr-3 text-xs">{r.companyCode}</td>
                                <td className="py-2 pr-3 text-xs">{r.country}</td>
                                <td className="py-2 pr-3 text-right font-mono">{formatEUR(r.totalEUR)}</td>
                                <td className="py-2 pr-3 text-right font-mono text-gray-400">{formatEUR(r.totalEURPY)}</td>
                                <td className="py-2 pr-3 text-right">
                                    {r.deltaPct !== null ? (
                                        <span className={r.deltaPct > 0 ? 'text-red-600' : r.deltaPct < 0 ? 'text-green-600' : ''}>
                                            {r.deltaPct > 0 ? '+' : ''}{r.deltaPct.toFixed(1)}%
                                        </span>
                                    ) : <span className="text-gray-300">-</span>}
                                </td>
                                <td className="py-2 text-right text-xs">{r.documentCount}</td>
                            </tr>
                        ))}
                        {vendorRows.length === 0 && (
                            <tr><td colSpan={7} className="py-8 text-center text-gray-400">No vendor data found</td></tr>
                        )}
                    </tbody>
                    {totals && vendorRows.length > 0 && (
                        <tfoot className="sticky bottom-0 z-10 bg-gray-50 shadow-[0_-1px_0_rgba(0,0,0,0.08)]">
                            <tr className="border-t-2 border-gray-300 font-semibold">
                                <td className="py-2 pr-3 bg-gray-50" colSpan={3}>Total ({pagination?.total ?? vendorRows.length} vendors)</td>
                                <td className="py-2 pr-3 text-right font-mono bg-gray-50">{formatEUR(totals.totalEUR)}</td>
                                <td className="py-2 pr-3 text-right font-mono text-gray-500 bg-gray-50">{formatEUR(totals.totalEURPY)}</td>
                                <td className="py-2 pr-3 text-right bg-gray-50">
                                    {totals.deltaPct !== null ? (
                                        <span className={totals.deltaPct > 0 ? 'text-red-600' : totals.deltaPct < 0 ? 'text-green-600' : ''}>
                                            {totals.deltaPct > 0 ? '+' : ''}{totals.deltaPct.toFixed(1)}%
                                        </span>
                                    ) : <span className="text-gray-300">-</span>}
                                </td>
                                <td className="bg-gray-50"></td>
                            </tr>
                        </tfoot>
                    )}
                </table>
                {renderPagination(pagination, page, setPage)}
                {renderDrillDown(drillDown, setDrillDown, filters)}
                {contextMenu && (
                    <ContextMenu
                        x={contextMenu.x}
                        y={contextMenu.y}
                        items={getContextMenuItems()}
                        onClose={() => setContextMenu(null)}
                    />
                )}
            </div>
        );
    }

    // Vendor Group mode table
    return (
        <div>
            {exportButton}
            <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_rgba(0,0,0,0.08)]">
                    <tr className="text-left text-xs font-medium text-gray-500 uppercase border-b">
                        <th className="py-2 pr-3 cursor-pointer bg-white" onClick={() => handleSort('groupName')}>
                            Vendor Group <SortIcon col="groupName" />
                        </th>
                        <th className="py-2 pr-3 text-center cursor-pointer bg-white" onClick={() => handleSort('memberCount')}>
                            Members <SortIcon col="memberCount" />
                        </th>
                        <th className="py-2 pr-3 text-center cursor-pointer bg-white" onClick={() => handleSort('companyCount')}>
                            Companies <SortIcon col="companyCount" />
                        </th>
                        <th className="py-2 pr-3 text-right cursor-pointer bg-white" onClick={() => handleSort('totalEUR')}>
                            Total EUR <SortIcon col="totalEUR" />
                        </th>
                        <th className="py-2 pr-3 text-right cursor-pointer bg-white" onClick={() => handleSort('totalEURPY')}>
                            PY EUR <SortIcon col="totalEURPY" />
                        </th>
                        <th className="py-2 text-right bg-white">Delta %</th>
                    </tr>
                </thead>
                <tbody>
                    {groupRows.map((r, i) => (
                        <tr
                            key={i}
                            className="border-b border-gray-100 hover:bg-blue-50 cursor-pointer"
                            onClick={() => setDrillDown({
                                groupVendorID: String(r.groupVendorID),
                                title: `Transactions: ${r.groupVendorName}`,
                            })}
                            onContextMenu={e => handleContextMenu(e, r)}
                        >
                            <td className="py-2 pr-3">
                                <span className="font-medium">{r.groupVendorName}</span>
                                {r.country && <span className="text-gray-400 ml-1">({r.country})</span>}
                            </td>
                            <td className="py-2 pr-3 text-center text-xs">{r.memberCount}</td>
                            <td className="py-2 pr-3 text-center text-xs">{r.companyCount}</td>
                            <td className="py-2 pr-3 text-right font-mono">{formatEUR(r.totalEUR)}</td>
                            <td className="py-2 pr-3 text-right font-mono text-gray-400">{formatEUR(r.totalEURPY)}</td>
                            <td className="py-2 text-right">
                                {r.deltaPct !== null ? (
                                    <span className={r.deltaPct > 0 ? 'text-red-600' : r.deltaPct < 0 ? 'text-green-600' : ''}>
                                        {r.deltaPct > 0 ? '+' : ''}{r.deltaPct.toFixed(1)}%
                                    </span>
                                ) : <span className="text-gray-300">-</span>}
                            </td>
                        </tr>
                    ))}
                    {groupRows.length === 0 && (
                        <tr><td colSpan={6} className="py-8 text-center text-gray-400">No vendor group data found</td></tr>
                    )}
                </tbody>
                {totals && groupRows.length > 0 && (
                    <tfoot className="sticky bottom-0 z-10 bg-gray-50 shadow-[0_-1px_0_rgba(0,0,0,0.08)]">
                        <tr className="border-t-2 border-gray-300 font-semibold">
                            <td className="py-2 pr-3 bg-gray-50" colSpan={3}>Total ({pagination?.total ?? groupRows.length} groups)</td>
                            <td className="py-2 pr-3 text-right font-mono bg-gray-50">{formatEUR(totals.totalEUR)}</td>
                            <td className="py-2 pr-3 text-right font-mono text-gray-500 bg-gray-50">{formatEUR(totals.totalEURPY)}</td>
                            <td className="py-2 text-right bg-gray-50">
                                {totals.deltaPct !== null ? (
                                    <span className={totals.deltaPct > 0 ? 'text-red-600' : totals.deltaPct < 0 ? 'text-green-600' : ''}>
                                        {totals.deltaPct > 0 ? '+' : ''}{totals.deltaPct.toFixed(1)}%
                                    </span>
                                ) : <span className="text-gray-300">-</span>}
                            </td>
                        </tr>
                    </tfoot>
                )}
            </table>
            {renderPagination(pagination, page, setPage)}
            {renderDrillDown(drillDown, setDrillDown, filters)}
            {contextMenu && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    items={getContextMenuItems()}
                    onClose={() => setContextMenu(null)}
                />
            )}
        </div>
    );
}

function renderPagination(pagination: PaginationInfo | null, _page: number, setPage: (fn: (p: number) => number) => void) {
    if (!pagination || pagination.totalPages <= 1) return null;
    return (
        <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
            <span>{pagination.total} entries total</span>
            <div className="flex items-center gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={!pagination.hasPrev} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30">
                    <ChevronLeft className="w-4 h-4" />
                </button>
                <span>Page {pagination.page} of {pagination.totalPages}</span>
                <button onClick={() => setPage(p => p + 1)} disabled={!pagination.hasNext} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30">
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}

function renderDrillDown(
    drillDown: { vendorID?: string; groupVendorID?: string; categoryCode?: string; title: string } | null,
    setDrillDown: (v: null) => void,
    filters: VCAFilters
) {
    if (!drillDown) return null;
    return (
        <TransactionDrillDownModal
            isOpen={true}
            onClose={() => setDrillDown(null)}
            title={drillDown.title}
            dateFrom={filters.dateFrom}
            dateTo={filters.dateTo}
            company={filters.company || undefined}
            vendorID={drillDown.vendorID}
            groupVendorID={drillDown.groupVendorID}
            // Inherit the page's active category filter so the modal stays consistent with the
            // current view; an explicit drillDown.categoryCode (from a context-menu action)
            // still wins if set.
            categoryCode={drillDown.categoryCode || filters.categoryCode || undefined}
        />
    );
}
